import * as DateFNS from 'date-fns';
import { useEventInfo } from '@shared/utils/eventInfo';
import { TravelCurrencyCode, useAccommodationPagePricingQuery, useAccommodationPagePricingForItemZHQuery } from '@graphql/generated';
import { useTravelCurrencyCode } from '../components/TravelMap/components/AccommodationsV2/components/useCurrencyCode';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';

const translatedPrice = (priceInMinorUnits: number, currency: string, locale: NavigatorLanguage['language'] = 'en-US') =>
  Math.floor(priceInMinorUnits / 100).toLocaleString(locale, {
    style: 'currency',
    maximumSignificantDigits: 3,
    currency
  });

const useHotelPricing = ({ joyPlaceId, showNuiteePrice = false }: { joyPlaceId: string; showNuiteePrice: boolean }) => {
  const { eventInfo } = useEventInfo();
  const dateMs = eventInfo?.finalizedDate?.milliseconds;
  const checkInDate = dateMs ? DateFNS.format(DateFNS.subDays(dateMs, 1), 'yyyy-MM-dd') : null;
  const checkOutDate = dateMs ? DateFNS.format(DateFNS.addDays(dateMs, 1), 'yyyy-MM-dd') : null;
  const { code: currencyCode, locale } = useTravelCurrencyCode();
  const { value: localeCurrencyOnOrOff, loading: loadingFeatureFlag } = useRemoteFeatureFlag('localeBasedHotelCurrency');

  // const showNuiteePrice = false;
  // TODO remove this JUNK after the experiment is over ("should we show Nuitee price?")
  const payload = {
    currencyCode: ((localeCurrencyOnOrOff === 'on' ? currencyCode : TravelCurrencyCode.USD) || TravelCurrencyCode.USD) as TravelCurrencyCode,
    dates: {
      checkIn: {
        // validated with skip
        high: checkInDate!,
        low: checkInDate!
      },
      // validated with skip
      checkOut: {
        high: checkOutDate!,
        low: checkOutDate!
      }
    },
    occupancies: {
      occupancies: [
        {
          adults: 2,
          childrenAges: []
        }
      ],
      unitCount: 1
    },
    propertyIdentifiers: [
      {
        joyPlaceId: joyPlaceId || null
      }
    ]
  };
  const { data: dataPricing, loading: loadingPricing } = useAccommodationPagePricingQuery({
    batchMode: 'slow',
    variables: {
      payload: payload
    },
    skip: loadingFeatureFlag || !checkInDate || !checkOutDate || !showNuiteePrice
  });
  const { data: dataPricingZH, loading: loadingZH } = useAccommodationPagePricingForItemZHQuery({
    batchMode: 'slow',
    variables: {
      payload: payload
    },
    skip: loadingFeatureFlag || !checkInDate || !checkOutDate || showNuiteePrice
  });
  const someOffers = dataPricingZH?.travelOffersForAccommodationProperties.someOffers;
  const lowestBaseOffer = someOffers && [...someOffers].sort((a, b) => a.baseAmount.valueInMinorUnits - b.baseAmount.valueInMinorUnits)[0];
  const priceZH = lowestBaseOffer?.baseAmount && translatedPrice(lowestBaseOffer.baseAmount.valueInMinorUnits, lowestBaseOffer.baseAmount.currency, locale);
  const originalPriceZH =
    lowestBaseOffer?.originalBaseAmount && translatedPrice(lowestBaseOffer.originalBaseAmount.valueInMinorUnits, lowestBaseOffer.originalBaseAmount.currency, locale);
  const totalAmountNuitee = dataPricing && dataPricing?.travelPricingsForAccommodationByPropertyId.pricings[0].pricingData?.perNightFinalAmountBeforeTaxesAndFees;
  const originalAmountNuitee = dataPricing && dataPricing?.travelPricingsForAccommodationByPropertyId.pricings[0].pricingData?.originalAmount;
  const priceNuitee = totalAmountNuitee && translatedPrice(totalAmountNuitee.valueInMinorUnits, totalAmountNuitee.currency, locale);
  const originalPriceNuitee = originalAmountNuitee && translatedPrice(originalAmountNuitee.valueInMinorUnits, originalAmountNuitee.currency, locale);

  const price = priceNuitee || priceZH;
  const loading = loadingZH || loadingPricing;
  const strikeOutPrice = originalPriceNuitee || originalPriceZH;

  return { price, loading, strikeOutPrice };
};

export default useHotelPricing;
